import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Globe, 
  Zap, 
  TrendingUp, 
  Award, 
  Briefcase, 
  Play,
  ChevronDown,
  ChevronUp,
  Lock,
  Network,
  Cpu,
  Brain,
  Rocket,
  MapPin,
  Clock
} from 'lucide-react';
import { FaNetworkWired } from 'react-icons/fa';

const BlockchainTechnologies = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  const services = [
    {
      icon: Shield,
      title: "Smart Contract Development",
      description: "Build secure, automated contracts that execute without intermediaries",
      features: ["Ethereum & Solidity", "Multi-chain Support", "Security Audits"]
    },
    {
      icon: Network,
      title: "DeFi Solutions",
      description: "Decentralized finance platforms for lending, trading, and yield farming",
      features: ["Liquidity Pools", "Yield Optimization", "Cross-chain Bridges"]
    },
    {
      icon: Lock,
      title: "Digital Identity Management",
      description: "Secure, verifiable digital identity solutions for enterprises",
      features: ["KYC/AML Compliance", "Self-Sovereign Identity", "Zero-Knowledge Proofs"]
    },
    {
      icon: Cpu,
      title: "Supply Chain Solutions",
      description: "Transparent tracking and verification throughout supply chains",
      features: ["Product Traceability", "Anti-counterfeiting", "Quality Assurance"]
    }
  ];

  const aiBlockchainFeatures = [
    {
      icon: Brain,
      title: "AI-Powered Smart Contracts",
      description: "Machine learning algorithms that optimize contract execution and predict outcomes"
    },
    {
      icon: TrendingUp,
      title: "Predictive Analytics",
      description: "AI models that analyze blockchain data to forecast market trends and behavior"
    },
    {
      icon: Shield,
      title: "Automated Security",
      description: "AI-driven security protocols that detect and prevent blockchain threats in real-time"
    }
  ];

  const careers = [
    {
      title: "Senior Blockchain Developer",
      location: "Remote / Hybrid",
      type: "Full-time",
      experience: "3+ years",
      skills: ["Solidity", "Web3.js", "Smart Contracts", "DeFi"]
    },
    {
      title: "Blockchain Solutions Architect",
      location: "Mumbai, India",
      type: "Full-time",
      experience: "5+ years",
      skills: ["Hyperledger", "Ethereum", "System Design", "Cloud Architecture"]
    },
    {
      title: "DApp Frontend Developer",
      location: "Bangalore, India",
      type: "Full-time",
      experience: "2+ years",
      skills: ["React", "Web3", "MetaMask Integration", "UI/UX"]
    }
  ];

  const trainingPrograms = [
    {
      title: "Blockchain Fundamentals",
      duration: "3 Months",
      level: "Beginner",
      price: "₹45,000",
      topics: ["Blockchain Basics", "Cryptocurrency", "Hash Functions", "Mining"]
    },
    {
      title: "Smart Contract Development",
      duration: "6 months",
      level: "Intermediate",
      price: "₹60,000",
      topics: ["Solidity Programming", "Contract Security", "Testing", "Deployment"]
    },
    {
      title: "DeFi Protocol Development",
      duration: "9 Months",
      level: "Advanced",
      price: "₹80,000",
      topics: ["AMM Development", "Yield Farming", "Flash Loans", "Governance"]
    }
  ];

  const faqs = [
    {
      question: "What is blockchain technology?",
      answer: "Blockchain is a distributed ledger technology that maintains a continuously growing list of records, called blocks, which are linked and secured using cryptography. Each block contains a cryptographic hash of the previous block, a timestamp, and transaction data."
    },
    {
      question: "How will blockchain transform applications by 2027-2028?",
      answer: "By 2027-2028, we expect widespread adoption of decentralized applications (DApps) across industries. Traditional centralized systems will increasingly migrate to blockchain-based solutions, offering better security, transparency, and user control over data."
    },
    {
      question: "What role does AI play in blockchain development?",
      answer: "AI enhances blockchain through automated smart contract optimization, predictive analytics for crypto markets, fraud detection, and intelligent consensus mechanisms. AI-driven blockchain applications will revolutionize data processing and decision-making."
    },
    {
      question: "What are the career prospects in blockchain?",
      answer: "Blockchain offers excellent career opportunities with roles like blockchain developers, smart contract auditors, DeFi specialists, and blockchain architects. The industry is experiencing rapid growth with competitive salaries and innovative projects."
    }
  ];

  const scrollToSection = (sectionId) => {
   
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-vh-100 bg-dark text-light">
     

      {/* Hero Section */}
      <section id="overview" className="min-vh-100 d-flex align-items-center position-relative overflow-hidden">
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-gradient-to-br from-dark via-dark to-info opacity-90"></div>
        <div className="container position-relative z-index-2">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="mb-4">
                <div className="d-inline-flex align-items-center bg-warning bg-opacity-10 text-warning px-4 py-2 rounded-pill mb-4">
                  <Zap size={20} className="me-2" />
                  Leading Blockchain Innovation
                </div>
                <h1 className="display-3 fw-bold mb-4 text-gradient">
                  Blockchain <span className="text-warning">Technologies</span>
                </h1>
                <p className="lead mb-4 text-light opacity-90">
                  Developing secure, decentralized systems for finance, supply chain, and digital identity management 
                  with transparent and immutable ledger solutions.
                </p>
                <div className="d-flex flex-wrap gap-3">
                  <button 
                    className="btn btn-warning btn-lg px-4 py-3 fw-semibold"
                    onClick={() => scrollToSection('services')}
                  >
                    Explore Services
                  </button>
                  <button 
                    className="btn btn-outline-light btn-lg px-4 py-3 fw-semibold"
                    onClick={() => scrollToSection('training')}
                  >
                    <Play size={20} className="me-2" />
                    Start Training
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="text-center">
                <div className="bg-warning bg-opacity-10 rounded-4 p-5 backdrop-blur">
                  <FaNetworkWired size={120} className="text-warning mb-4" />
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="bg-dark bg-opacity-50 rounded-3 p-3">
                        <Shield size={40} className="text-warning mb-2" />
                        <small className="text-light">Secure</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-dark bg-opacity-50 rounded-3 p-3">
                        <Network size={40} className="text-warning mb-2" />
                        <small className="text-light">Decentralized</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-dark bg-opacity-50 rounded-3 p-3">
                        <Lock size={40} className="text-warning mb-2" />
                        <small className="text-light">Immutable</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-dark bg-opacity-50 rounded-3 p-3">
                        <Globe size={40} className="text-warning mb-2" />
                        <small className="text-light">Transparent</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-5 bg-light text-dark">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="display-4 fw-bold mb-4">Our <span className="text-warning">Services</span></h2>
            <p className="lead text-dark opacity-90 mx-auto" style={{maxWidth: '600px'}}>
              Comprehensive blockchain solutions tailored for modern businesses and future-ready applications
            </p>
          </div>
          <div className="row g-4">
            {services.map((service, index) => (
              <div key={index} className="col-lg-6">
                <div className="card bg-dark border-warning h-100 shadow-lg hover-lift">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-warning bg-opacity-10 rounded-3 p-3 me-3">
                        <service.icon size={32} className="text-warning" />
                      </div>
                      <h4 className="mb-0 text-light">{service.title}</h4>
                    </div>
                    <p className="text-light opacity-80 mb-3">{service.description}</p>
                    <ul className="list-unstyled">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="d-flex align-items-center mb-2">
                          <div className="bg-warning rounded-circle me-2" style={{width: '8px', height: '8px'}}></div>
                          <span className="text-light">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Scope Section */}
      <section id="future" className="py-5 bg-dark">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="display-4 fw-bold mb-4">Future <span className="text-warning">Scope</span></h2>
            <p className="lead text-light opacity-90 mx-auto" style={{maxWidth: '700px'}}>
              The blockchain revolution is just beginning. By 2027-2028, decentralized applications will transform 
              every industry, powered by AI-driven innovations.
            </p>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-lg-4">
              <div className="text-center">
                <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                     style={{width: '80px', height: '80px'}}>
                  <TrendingUp size={40} className="text-warning" />
                </div>
                <h4 className="text-warning mb-3">2025-2026</h4>
                <p className="text-light opacity-80">
                  Mass adoption of DeFi protocols, NFT marketplaces, and Web3 social platforms. 
                  Enterprise blockchain integration accelerates.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="text-center">
                <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                     style={{width: '80px', height: '80px'}}>
                  <Rocket size={40} className="text-warning" />
                </div>
                <h4 className="text-warning mb-3">2027-2028</h4>
                <p className="text-light opacity-80">
                  Complete transformation to decentralized applications. AI-blockchain convergence 
                  creates intelligent, autonomous systems.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="text-center">
                <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                     style={{width: '80px', height: '80px'}}>
                  <Brain size={40} className="text-warning" />
                </div>
                <h4 className="text-warning mb-3">2029+</h4>
                <p className="text-light opacity-80">
                  Fully autonomous blockchain ecosystems with AI governance, 
                  self-healing smart contracts, and quantum-resistant security.
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <h3 className="text-center mb-4">AI-Driven Blockchain Applications</h3>
              <div className="row g-4">
                {aiBlockchainFeatures.map((feature, index) => (
                  <div key={index} className="col-lg-4">
                    <div className="card bg-dark border-1 h-100">
                      <div className="card-body text-center p-4">
                        <feature.icon size={48} className="text-warning mb-3" />
                        <h5 className="text-light mb-3">{feature.title}</h5>
                        <p className="text-light opacity-80">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section id="careers" className="py-5 bg-light">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="display-4 fw-bold mb-4 text-dark ">Career <span className="text-warning">Opportunities</span></h2>
            <p className="lead text-dark opacity-90 mx-auto" style={{maxWidth: '600px'}}>
              Join our team of blockchain innovators and shape the future of decentralized technology
            </p>
          </div>
          <div className="row g-4">
            {careers.map((job, index) => (
              <div key={index} className="col-lg-4">
                <div className="card bg-dark border-warning h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-3">
                      <Briefcase size={24} className="text-warning me-2" />
                      <span className="badge bg-warning text-dark">{job.type}</span>
                    </div>
                    <h5 className="text-light mb-3">{job.title}</h5>
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <MapPin size={16} className="text-warning me-2" />
                        <small className="text-light opacity-80">{job.location}</small>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <Award size={16} className="text-warning me-2" />
                        <small className="text-light opacity-80">{job.experience}</small>
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-warning fw-semibold">Required Skills:</small>
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        {job.skills.map((skill, idx) => (
                          <span key={idx} className="badge bg-warning bg-opacity-10 text-warning">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button className="btn btn-warning w-100">Apply Now</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Section */}
      <section id="training" className="py-5 bg-dark">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="display-4 fw-bold mb-4">Training <span className="text-warning">Programs</span></h2>
            <p className="lead text-light opacity-90 mx-auto" style={{maxWidth: '600px'}}>
              Master blockchain technology with our comprehensive training programs designed for all skill levels
            </p>
                        <p>All Our Training Program are Industry Aligned Real Time Projects. Trainers from Top IIT's, Founders, Industry Experts  </p>

          </div>
          <div className="row g-4">
            {trainingPrograms.map((program, index) => (
              <div key={index} className="col-lg-4">
                <div className="card bg-secondary border-warning h-100">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="badge bg-warning text-dark">{program.level}</span>
                      <div className="d-flex align-items-center text-warning">
                        <Clock size={16} className="me-1" />
                        <small>{program.duration}</small>
                      </div>
                    </div>
                    <h5 className="text-light mb-3">{program.title}</h5>
                    <div className="mb-3">
                      <small className="text-warning fw-semibold">Topics Covered:</small>
                      <ul className="list-unstyled mt-2">
                        {program.topics.map((topic, idx) => (
                          <li key={idx} className="d-flex align-items-center mb-1">
                            <div className="bg-warning rounded-circle me-2" style={{width: '6px', height: '6px'}}></div>
                            <small className="text-light opacity-80">{topic}</small>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="h5 text-warning mb-0">{program.price}</span>
                      <button className="btn btn-warning">Enroll Now</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-5 bg-secondary">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="display-4 fw-bold mb-4">Frequently Asked <span className="text-warning">Questions</span></h2>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {faqs.map((faq, index) => (
                <div key={index} className="card bg-dark border-warning mb-3">
                  <div className="card-header bg-transparent border-warning p-0">
                    <button
                      className="btn btn-link text-light text-decoration-none w-100 text-start p-4 d-flex justify-content-between align-items-center"
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    >
                      <span className="fw-semibold">{faq.question}</span>
                      {expandedFaq === index ? 
                        <ChevronUp size={20} className="text-warning" /> : 
                        <ChevronDown size={20} className="text-warning" />
                      }
                    </button>
                  </div>
                  {expandedFaq === index && (
                    <div className="card-body">
                      <p className="text-light opacity-90 mb-0">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


    </div>
  )
}
export default BlockchainTechnologies;